import React, { useEffect, useRef, useState } from 'react';
import { Mic, Pause, Play, Square, AlertCircle } from 'lucide-react';

// Phase F6 — probe the MIME types this browser can actually record.
// Chrome/Firefox → audio/webm(opus); Safari/iOS 14.3+ → audio/mp4 (AAC).
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

export const pickSupportedMime = () => {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch { /* noop */ }
  }
  return '';
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

/**
 * AudioRecorder — Phase F6. MediaRecorder with native Pausa/Riprendi:
 * one recorder instance, one final Blob (never multiple racconti).
 * Never publishes anything: hands the blob to the parent for preview.
 */
const AudioRecorder = ({ onComplete, onRecordingChange }) => {
  const [phase, setPhase] = useState('idle'); // idle | asking | recording | paused
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const secondsRef = useRef(0);

  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  const startTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      secondsRef.current += 1;
      setSeconds(secondsRef.current);
    }, 1000);
  };
  const cleanupStream = () => {
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    streamRef.current = null;
  };

  useEffect(() => () => { stopTimer(); cleanupStream(); }, []);
  useEffect(() => { onRecordingChange?.(phase === 'recording' || phase === 'paused'); }, [phase, onRecordingChange]);

  const start = async () => {
    setError('');
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Questo browser non supporta la registrazione audio. Puoi comunque caricare un file audio.');
      return;
    }
    setPhase('asking');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setPhase('idle');
      if (e?.name === 'NotAllowedError' || e?.name === 'SecurityError') {
        setError('Per registrare il Racconto, consenti a Nymia di utilizzare il microfono.');
      } else if (e?.name === 'NotFoundError' || e?.name === 'OverconstrainedError') {
        setError('Nessun microfono disponibile su questo dispositivo.');
      } else {
        setError('Impossibile avviare la registrazione. Riprova.');
      }
      return;
    }
    streamRef.current = stream;
    const mime = pickSupportedMime();
    let rec;
    try {
      rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      try {
        rec = new MediaRecorder(stream);
      } catch {
        cleanupStream();
        setPhase('idle');
        setError('Questo browser non supporta la registrazione audio. Puoi comunque caricare un file audio.');
        return;
      }
    }
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onerror = () => {
      stopTimer();
      cleanupStream();
      setPhase('idle');
      setError('La registrazione si è interrotta per un errore. Riprova.');
    };
    rec.onstop = () => {
      stopTimer();
      cleanupStream();
      const type = rec.mimeType || mime || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      const dur = secondsRef.current;
      secondsRef.current = 0;
      setSeconds(0);
      setPhase('idle');
      if (blob.size > 0) onComplete?.(blob, type, dur);
      else setError('Registrazione vuota. Riprova.');
    };
    recRef.current = rec;
    secondsRef.current = 0;
    setSeconds(0);
    rec.start(1000);
    setPhase('recording');
    startTimer();
  };

  const pause = () => {
    try { recRef.current?.pause(); } catch { /* noop */ }
    stopTimer();
    setPhase('paused');
  };
  const resume = () => {
    try { recRef.current?.resume(); } catch { /* noop */ }
    startTimer();
    setPhase('recording');
  };
  const stop = () => {
    try { recRef.current?.stop(); } catch { setPhase('idle'); }
  };

  return (
    <div className="rounded-xl border border-border bg-background p-4" data-testid="audio-recorder">
      {error && (
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-500" data-testid="recorder-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {(phase === 'idle' || phase === 'asking') && (
        <button
          onClick={start}
          disabled={phase === 'asking'}
          className="flex w-full items-center justify-center gap-2 rounded-full gold-gradient px-5 py-3 text-sm font-bold text-black disabled:opacity-60"
          data-testid="recorder-start-btn"
          aria-label="Inizia registrazione"
        >
          <Mic className="h-4 w-4" />
          {phase === 'asking' ? 'Attivazione microfono…' : 'Inizia registrazione'}
        </button>
      )}

      {(phase === 'recording' || phase === 'paused') && (
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-2" data-testid="recorder-status">
            {phase === 'recording' ? (
              <>
                <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wider text-red-500">Registrazione</span>
              </>
            ) : (
              <>
                <Pause className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">In pausa</span>
              </>
            )}
          </div>
          <p className="text-3xl font-bold tabular-nums" data-testid="recorder-timer">{fmt(seconds)}</p>
          <div className="flex items-center gap-3">
            {phase === 'recording' ? (
              <button
                onClick={pause}
                className="flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold hover:bg-secondary"
                data-testid="recorder-pause-btn"
                aria-label="Pausa"
              >
                <Pause className="h-3.5 w-3.5" /> Pausa
              </button>
            ) : (
              <button
                onClick={resume}
                className="flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-xs font-semibold hover:bg-secondary"
                data-testid="recorder-resume-btn"
                aria-label="Riprendi"
              >
                <Play className="h-3.5 w-3.5" /> Riprendi
              </button>
            )}
            <button
              onClick={stop}
              className="flex items-center gap-1.5 rounded-full bg-red-500 px-4 py-2 text-xs font-bold text-white hover:bg-red-600"
              data-testid="recorder-stop-btn"
              aria-label="Termina registrazione"
            >
              <Square className="h-3.5 w-3.5" /> Termina
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioRecorder;
